import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Edit, PersonAdd, Search, Security } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { hasAnySupportRole } from 'src/supportPortal/access/accessMatrix'
import {
  listAuditorProfiles,
  revokeAuditorRole,
} from 'src/supportPortal/services/auditorManagementService'
import { AUDITOR_ROLE_LABELS } from 'src/supportPortal/views/serviceApprovals/utils/auditorConstants'
import AuditorAssignmentDialog from './AuditorAssignmentDialog'

const MANAGE_ROLES = ['ROLE_THERAPIST_ADMIN', 'ROLE_ADMIN']

function profilePractitionerName(view) {
  const practitioner = view?.practitioner || {}
  const user = view?.user || {}
  return practitioner.name || practitioner.title || user.name || user.userName || 'Practitioner'
}

function permissionSummary(permission) {
  const parts = [permission.eventType, AUDITOR_ROLE_LABELS[permission.roleId] || permission.roleId]
  if (permission.defaultAuditorType) parts.push(permission.defaultAuditorType)
  if (permission.roleExpiry) parts.push(`expires ${permission.roleExpiry}`)
  if (permission.enabled === false) parts.push('disabled')
  return parts.filter(Boolean).join(' · ')
}

export default function AuditorManagementPanel({ userRoles = [] }) {
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editProfile, setEditProfile] = useState(null)
  const [revokeTarget, setRevokeTarget] = useState(null)
  const [revoking, setRevoking] = useState(false)

  const canManage = hasAnySupportRole(userRoles, MANAGE_ROLES)

  async function load() {
    setLoading(true)
    try {
      const data = await listAuditorProfiles()
      setProfiles(Array.isArray(data) ? data : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return profiles
    return profiles.filter((view) => {
      const haystack = [
        profilePractitionerName(view),
        view?.practitioner?.slug,
        view?.user?.email,
        view?.user?.mobile,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [profiles, search])

  function openAssign() {
    setEditProfile(null)
    setDialogOpen(true)
  }

  function openEdit(view) {
    setEditProfile(view)
    setDialogOpen(true)
  }

  async function confirmRevoke() {
    if (!revokeTarget) return
    setRevoking(true)
    try {
      const { view, roleId } = revokeTarget
      const result = await revokeAuditorRole({
        userId: view?.auditorProfile?.userId || view?.user?.userId,
        practitionerId: view?.auditorProfile?.practitionerId || view?.practitioner?.practitionerId,
        roleId,
      })
      if (result.ok) {
        toast.success(`Revoked ${AUDITOR_ROLE_LABELS[roleId] || roleId}`)
        setRevokeTarget(null)
        load()
      } else {
        toast.error(result.message)
      }
    } finally {
      setRevoking(false)
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search auditors..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flexGrow: 1, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {canManage && (
          <Button variant="contained" startIcon={<PersonAdd />} onClick={openAssign}>
            Assign Auditor Role
          </Button>
        )}
      </Box>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Assigning, editing and revoking auditor roles requires Therapist Admin. You can view the
          current auditors below.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info" icon={<Security />}>
          No auditors found. {canManage ? 'Use "Assign Auditor Role" to make a practitioner an auditor.' : ''}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((view) => {
            const profile = view.auditorProfile || {}
            const roleIds = Array.isArray(profile.auditorRoleIds) ? profile.auditorRoleIds : []
            const permissions = Array.isArray(profile.eventTypePermissions)
              ? profile.eventTypePermissions
              : []
            const isDisabled = profile.enabled === false || roleIds.length === 0
            return (
              <Paper
                key={profile.practitionerId || profile.userId || view?.user?.userId}
                variant="outlined"
                sx={{ p: 2, opacity: isDisabled ? 0.7 : 1 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {profilePractitionerName(view)}
                      {isDisabled && (
                        <Chip size="small" label="Disabled" sx={{ ml: 1 }} color="default" variant="outlined" />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[view?.practitioner?.slug, view?.user?.email, view?.user?.mobile]
                        .filter(Boolean)
                        .join(' | ')}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {roleIds.length === 0 ? (
                        <Chip size="small" label="No active roles" variant="outlined" />
                      ) : (
                        roleIds.map((roleId) => (
                          <Chip
                            key={roleId}
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={AUDITOR_ROLE_LABELS[roleId] || roleId}
                            onDelete={canManage ? () => setRevokeTarget({ view, roleId }) : undefined}
                          />
                        ))
                      )}
                    </Box>
                  </Box>
                  {canManage && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Tooltip title="Edit auditor settings">
                        <Button size="small" startIcon={<Edit />} onClick={() => openEdit(view)}>
                          Edit
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {permissions.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={0.5}>
                      {permissions.map((permission, index) => (
                        <Typography
                          key={`${permission.roleId}-${permission.eventType}-${index}`}
                          variant="body2"
                          color={permission.enabled === false ? 'text.disabled' : 'text.secondary'}
                        >
                          • {permissionSummary(permission)}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                )}
              </Paper>
            )
          })}
        </Stack>
      )}

      {dialogOpen && (
        <AuditorAssignmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={load}
          existingProfile={editProfile}
        />
      )}

      <Dialog open={Boolean(revokeTarget)} onClose={revoking ? undefined : () => setRevokeTarget(null)}>
        <DialogTitle>Revoke auditor role?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes{' '}
            <strong>{AUDITOR_ROLE_LABELS[revokeTarget?.roleId] || revokeTarget?.roleId}</strong> from{' '}
            <strong>{profilePractitionerName(revokeTarget?.view)}</strong> and updates their auditor profile.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRevoke}
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={16} /> : null}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
